import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { AudioLines, Loader2, Maximize2, Plus, Square, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { UserRole } from '../../lib/types/auth';
import { ClaireService } from '../../services/claireService';
import { END_PHRASE, HandsFreeEngine, WAKE_PHRASE, type HandsFreeStatus } from '../../lib/claire/handsFree';
import { cn } from '../../utils/cn';
import { onClaireAvatarError, useClaireAvatar } from '../../lib/claire/useClaireAvatar';
import { ClaireConversation } from './ClaireConversation';

export function useClaireAccess(): boolean {
  const { currentUser } = useAuth();
  return !!currentUser && (currentUser.roles?.includes(UserRole.MasterAdmin) || currentUser.claireAccess === true);
}

/** Window events that open/close the Claire panel (fired from her icon in Active Users). */
export const CLAIRE_BUBBLE_TOGGLE = 'claire-bubble:toggle';
export const CLAIRE_BUBBLE_CLOSE = 'claire-bubble:close';

/** Where the live spot-price (melt) bar is, so Claire sits above it or beside its flame button. */
export type MeltBarPlacement = 'open' | 'closed' | 'none';

const MELT_BAR_HEIGHT = 56;
const FLAME_BUTTON = { right: 16, size: 44, bottom: 16 };
const BUTTON_HEIGHT = 48;
const PLACEMENT: Record<MeltBarPlacement, { bottom: number; right: number }> = {
  open: { bottom: MELT_BAR_HEIGHT + 12, right: 20 },
  closed: { bottom: FLAME_BUTTON.bottom + (FLAME_BUTTON.size - BUTTON_HEIGHT) / 2, right: FLAME_BUTTON.right + FLAME_BUTTON.size + 10 },
  none: { bottom: 16, right: 16 },
};

const ClaireFace: React.FC<{ size: number; className?: string }> = ({ size, className }) => (
  <img
    src={useClaireAvatar()}
    onError={onClaireAvatarError}
    alt=""
    width={size}
    height={size}
    draggable={false}
    className={cn('rounded-full object-cover shrink-0 select-none', className)}
    style={{ width: size, height: size }}
  />
);

const Switch: React.FC<{ on: boolean; onChange: (v: boolean) => void; label: string }> = ({ on, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    onClick={() => onChange(!on)}
    className={cn('relative inline-flex h-[18px] w-8 shrink-0 rounded-full transition-colors', on ? 'bg-[#241813]' : 'bg-[#d9cfc0]')}
  >
    <span className={cn('absolute top-[2px] h-[14px] w-[14px] rounded-full bg-white shadow transition-all', on ? 'left-[16px]' : 'left-[2px]')} />
  </button>
);

const LIVE_LABEL: Partial<Record<HandsFreeStatus['state'], string>> = {
  listening: 'Listening…',
  thinking: 'Thinking…',
  speaking: 'Speaking',
};

/** Floating Claire chat for users a Master Admin has enabled in User Management. */
export const ClaireBubble: React.FC<{ meltBar?: MeltBarPlacement }> = ({ meltBar = 'none' }) => {
  const { currentUser } = useAuth();
  const hasAccess = useClaireAccess();
  const location = useLocation();
  const navigate = useNavigate();
  const uid = currentUser?.uid || '';
  const storageKey = `claireBubbleSession:${uid}`;
  const handsFreeKey = `claireHandsFree:${uid}`;
  const sharedKey = `claireHandsFreeShared:${uid}`;
  const [open, setOpen] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(() => localStorage.getItem(storageKey));
  const [handsFree, setHandsFree] = useState(() => localStorage.getItem(handsFreeKey) === '1');
  const [shared, setShared] = useState(() => localStorage.getItem(sharedKey) === '1');
  const [hf, setHf] = useState<HandsFreeStatus>({ state: 'off' });
  const [flash, setFlash] = useState<string | null>(null);
  const engineRef = useRef<HandsFreeEngine | null>(null);
  const sessionRef = useRef(sessionId);
  const sharedRef = useRef(shared);
  sessionRef.current = sessionId;
  sharedRef.current = shared;

  useEffect(() => {
    setSessionId(localStorage.getItem(storageKey));
    setHandsFree(localStorage.getItem(handsFreeKey) === '1');
    setShared(localStorage.getItem(sharedKey) === '1');
  }, [storageKey, handsFreeKey, sharedKey]);

  const updateSession = (id: string | null) => {
    sessionRef.current = id;
    setSessionId(id);
    if (id) localStorage.setItem(storageKey, id);
    else localStorage.removeItem(storageKey);
  };
  const updateSessionRef = useRef(updateSession);
  updateSessionRef.current = updateSession;

  const toggleHandsFree = (on: boolean) => {
    setHandsFree(on);
    setFlash(null);
    if (on) localStorage.setItem(handsFreeKey, '1');
    else localStorage.removeItem(handsFreeKey);
  };
  const toggleShared = (on: boolean) => {
    setShared(on);
    if (on) localStorage.setItem(sharedKey, '1');
    else localStorage.removeItem(sharedKey);
  };

  useEffect(() => {
    if (!hasAccess || !handsFree) return;
    const engine = new HandsFreeEngine({
      getSessionId: () => {
        if (sessionRef.current) return sessionRef.current;
        const id = ClaireService.newSessionId();
        updateSessionRef.current(id);
        return id;
      },
      shared: () => sharedRef.current,
      onStatus: (s) => {
        setHf(s);
        if (s.state === 'off' && s.notice) {
          setHandsFree(false);
          localStorage.removeItem(handsFreeKey);
        }
      },
    });
    engineRef.current = engine;
    void engine.start();
    return () => {
      engine.stop();
      engineRef.current = null;
    };
  }, [hasAccess, handsFree, handsFreeKey]);

  useEffect(() => {
    const onToggle = () => setOpen((o) => !o);
    const onClose = () => setOpen(false);
    window.addEventListener(CLAIRE_BUBBLE_TOGGLE, onToggle);
    window.addEventListener(CLAIRE_BUBBLE_CLOSE, onClose);
    return () => {
      window.removeEventListener(CLAIRE_BUBBLE_TOGGLE, onToggle);
      window.removeEventListener(CLAIRE_BUBBLE_CLOSE, onClose);
    };
  }, []);

  useEffect(() => {
    const text = hf.notice || (hf.state === 'waiting' && hf.answer && !open ? hf.answer : null);
    if (!text) return;
    setFlash(text);
    const t = setTimeout(() => setFlash((f) => (f === text ? null : f)), hf.state === 'off' ? 10000 : 6000);
    return () => clearTimeout(t);
  }, [hf, open]);

  if (!hasAccess) return null;

  const onClairePage = location.pathname.startsWith('/claire') || location.pathname.startsWith('/admin/claire');
  const pos = PLACEMENT[meltBar];
  const panelRight = meltBar === 'closed' ? 16 : pos.right;
  const panelBottom = meltBar === 'open' ? pos.bottom : 16;
  const live = LIVE_LABEL[hf.state];
  const wakePhrase = hf.wakePhrase || WAKE_PHRASE;
  const firstName = (currentUser?.displayName || '').split(' ')[0];

  const pill = (live || flash) && !open && (
    <div
      className="fixed z-[9998] max-w-[320px] rounded-2xl border border-[#e7ded0] bg-white/95 backdrop-blur px-3.5 py-2.5 shadow-xl"
      style={onClairePage ? { bottom: 16, right: 16 } : { bottom: panelBottom, right: pos.right }}
    >
      <div className="flex items-start gap-2.5">
        <ClaireFace size={28} className={cn(hf.state === 'listening' && 'ring-2 ring-emerald-400 animate-pulse')} />
        <button className="flex-1 min-w-0 text-left" onClick={() => !onClairePage && setOpen(true)} title="Open Claire">
          {live ? (
            <>
              <p className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[#241813]">
                {hf.state === 'thinking' && <Loader2 className="w-3 h-3 animate-spin" />}
                {live}
              </p>
              {hf.state !== 'listening' && hf.heard && <p className="mt-0.5 text-[12px] text-[#7a6a5a] line-clamp-2">“{hf.heard}”</p>}
              {hf.state === 'speaking' && hf.answer && <p className="mt-1 text-[12.5px] text-[#241813] line-clamp-3">{hf.answer}</p>}
              {hf.conversing && <p className="mt-1 text-[11px] text-[#9a8a78]">Say “{END_PHRASE}” to finish.</p>}
            </>
          ) : (
            <p className="text-[12.5px] text-[#241813] line-clamp-4">{flash}</p>
          )}
        </button>
        {hf.state === 'speaking' && (
          <button onClick={() => engineRef.current?.hush()} title="Stop" className="p-1 rounded-md text-[#8a7a6a] hover:bg-[#f4eee4] hover:text-[#241813]">
            <Square className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </div>
  );

  if (onClairePage) return <>{pill}</>;

  return (
    <>
      {pill}
      {open && (
        <div
          className="fixed z-[9998] w-[400px] max-w-[calc(100vw-2rem)] h-[600px] flex flex-col rounded-2xl border border-[#e7ded0] bg-[#fdfbf7] shadow-2xl overflow-hidden"
          style={{ bottom: panelBottom, right: panelRight, maxHeight: `calc(100vh - ${panelBottom + 24}px)` }}
        >
          <div className="flex items-center gap-2.5 px-4 py-3 border-b border-[#efe7da] bg-white">
            <ClaireFace size={28} />
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-semibold text-[#241813] leading-tight">Claire</p>
              <p className="text-[11px] text-[#9a8a78] leading-tight">FIN assistant</p>
            </div>
            <button onClick={() => updateSession(null)} title="New conversation" className="p-1.5 rounded-md text-[#8a7a6a] hover:bg-[#f4eee4] hover:text-[#241813]">
              <Plus className="w-4 h-4" />
            </button>
            <button
              onClick={() => {
                setOpen(false);
                navigate(sessionId ? `/claire?session=${sessionId}` : '/claire');
              }}
              title="Open Claire workspace"
              className="p-1.5 rounded-md text-[#8a7a6a] hover:bg-[#f4eee4] hover:text-[#241813]"
            >
              <Maximize2 className="w-4 h-4" />
            </button>
            <button onClick={() => setOpen(false)} title="Close" className="p-1.5 rounded-md text-[#8a7a6a] hover:bg-[#f4eee4] hover:text-[#241813]">
              <X className="w-4 h-4" />
            </button>
          </div>
          <div className="px-4 py-2 border-b border-[#efe7da] bg-[#faf7f2] space-y-1.5">
            <div className="flex items-center gap-2">
              <AudioLines className={cn('w-3.5 h-3.5', handsFree ? 'text-emerald-600' : 'text-[#9a8a78]')} />
              <span className="flex-1 text-[12px] text-[#5a4a3a]">
                {!handsFree
                  ? 'Hands-free'
                  : live
                    ? `Hands-free · ${live}`
                    : hf.state === 'waiting'
                      ? `Say “${wakePhrase}”`
                      : hf.notice || 'Starting…'}
              </span>
              <Switch on={handsFree} onChange={toggleHandsFree} label="Hands-free" />
            </div>
            {handsFree && (
              <div className="flex items-center gap-2 pl-[22px]">
                <span className="flex-1 text-[11.5px] text-[#8a7a6a]" title="Customers may hear Claire. She won’t say customer details or cost figures out loud.">
                  Shared device (store counter)
                </span>
                <Switch on={shared} onChange={toggleShared} label="Shared device" />
              </div>
            )}
            {!handsFree && flash && <p className="text-[11.5px] text-amber-800">{flash}</p>}
          </div>
          <div className="flex-1 min-h-0">
            <ClaireConversation
              sessionId={sessionId}
              onSessionId={updateSession}
              source="bubble"
              compact
              autoFocus
              emptyState={
                <div className="pt-10 text-center px-6">
                  <ClaireFace size={56} className="mx-auto" />
                  <p className="mt-4 text-[15px] font-semibold text-[#241813]">Hi{firstName ? `, ${firstName}` : ''}.</p>
                  <p className="mt-1 text-[13px] text-[#8a7a6a]">
                    {handsFree ? `Type below, or just say “${wakePhrase}”.` : 'What can I take care of?'}
                  </p>
                </div>
              }
            />
          </div>
        </div>
      )}
    </>
  );
};
